/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import * as React from 'react';
import { OccupantByPermission, UserPermissions } from '@centecomm/portal-service';
import { LoadingContent } from 'Shared/PageElements';
import { useTranslation } from 'react-i18next';
import { Close, List } from '../../../Icons';
import { GenericSelection } from '..';
import { PropertySelectState } from '../../../Types';
import { OccupantsList } from './OccupantsList';
import { PropertySelectStateIcon, PropertySelectStateText } from './PropertySelectState';
import { usePropertyTenantResolution } from '../usePropertyTenantResolution';
import styles from './state-property.module.css';

interface StatePropertyProps {
    id: number;
    name: string;
    occupants: OccupantByPermission[];
    occupantIds: number[];
    showSelectedOnly: boolean;
    toggleOccupants: (occupantIds: number[], operator?: string) => void;
    selectedOccupants: Set<number>;
    visibleOccupants: Set<number>;
    permission?: UserPermissions;
}

function StateProperty({
    id,
    name,
    occupants,
    occupantIds,
    showSelectedOnly,
    toggleOccupants,
    selectedOccupants,
    visibleOccupants,
    permission = UserPermissions.CreateNews,
}: StatePropertyProps): React.ReactElement<StatePropertyProps> {
    const { t } = useTranslation(['forms']);
    const occupantCount = occupantIds.length;

    const [showOccupants, toggleShowOccupants] = React.useState<boolean>(false);
    const [selectedOccupantCount, setSelectedOccupantCount] = React.useState<number>(0);
    const [selectState, setPropertySelectState] = React.useState<PropertySelectState>(PropertySelectState.NONE);

    const [, loadOccupants, occupantsAreLoaded] = usePropertyTenantResolution(permission);

    React.useEffect(() => {
        const selectedCount: number = occupantIds.filter((_) => selectedOccupants.has(_)).length;

        setSelectedOccupantCount(selectedCount);

        switch (occupantCount - selectedCount) {
            case occupantCount:
                setPropertySelectState(PropertySelectState.NONE);
                break;
            case 0:
                setPropertySelectState(PropertySelectState.ALL);
                break;
            default:
                setPropertySelectState(PropertySelectState.SOME);
                break;
        }
    }, [selectedOccupants]);

    const selectProperty = (): void => {
        toggleOccupants(occupantIds, 'add');
    };

    const deselectProperty = (): void => {
        toggleOccupants(occupantIds, 'remove');
    };

    const shouldShowSelf = (): boolean => {
        // When selected only is on: If the propertyState is not NONE
        if (showSelectedOnly && selectState === PropertySelectState.NONE) {
            return false;
        }

        // Default: Show all
        return true;
    };

    const toggleShowOccupantsHandler = () => {
        loadOccupants(id);
        toggleShowOccupants(!showOccupants);
    };

    return (
        <>
            {shouldShowSelf() ? (
                <div className={styles.StatePropertyWrapper}>
                    <div className={styles.StateProperty}>
                        <div className={styles.PropertyName}>
                            <h2>{name}</h2>
                        </div>
                        <div
                            className={styles.StatePropertyActions}
                            onClick={toggleShowOccupantsHandler}
                            data-qa="select-tenant-from-list"
                        >
                            <div
                                className={`${styles.StatePropertySelectIcon} ${
                                    selectState === PropertySelectState.NONE
                                        ? styles.InActiveProperty
                                        : styles.ActiveProperty
                                }`}
                            >
                                <PropertySelectStateIcon state={selectState} count={selectedOccupantCount} />
                            </div>
                            <PropertySelectStateText state={selectState} count={selectedOccupantCount} />
                            <div className={styles.StatePropertyShowList}>
                                {showOccupants ? <Close aspect="1.5rem" /> : <List />}
                            </div>
                        </div>
                    </div>
                    {showOccupants ? (
                        occupantsAreLoaded ? (
                            <>
                                <ul className={styles.OccupantsList}>
                                    {!showSelectedOnly ? (
                                        selectState === PropertySelectState.ALL ? (
                                            <GenericSelection
                                                id={id}
                                                isActive
                                                toggleSelection={deselectProperty}
                                                display={t('forms:selectors.tenants.removeAll')}
                                            />
                                        ) : (
                                            <GenericSelection
                                                id={id}
                                                isActive={false}
                                                toggleSelection={selectProperty}
                                                display={t('forms:selectors.tenants.selectAll')}
                                            />
                                        )
                                    ) : null}
                                    <OccupantsList
                                        occupants={occupants}
                                        showSelectedOnly={showSelectedOnly}
                                        visibleOccupants={visibleOccupants}
                                        selectedOccupants={selectedOccupants}
                                        toggleOccupants={toggleOccupants}
                                    />
                                </ul>
                            </>
                        ) : (
                            <LoadingContent />
                        )
                    ) : null}
                </div>
            ) : null}
        </>
    );
}

export default StateProperty;
